import React from "react";
import { Plus } from "lucide-react";

export default function CommunityStartThreadBar({
  user,
  activeTopicTag = "All topics",
  onClickPost,
  size = 40,
}) {
  const displayName = getDisplayName(user);

  return (
    <div className="px-4 py-3">
      <button
        type="button"
        onClick={onClickPost}
        className="
          group w-full flex items-center gap-4
          rounded-2xl border border-white/12
          bg-black/35 hover:bg-black/50
          px-4 py-3
          transition-all duration-200
          text-left
          backdrop-blur-xl
          shadow-[0_12px_34px_rgba(0,0,0,0.55)]
          hover:shadow-[0_18px_44px_rgba(0,0,0,0.65)]
          hover:-translate-y-[1px]
          focus-visible:outline-none
          focus-visible:ring-2 focus-visible:ring-emerald-400/40
          focus-visible:ring-offset-0
        "
        aria-label="Start a thread"
      >
        <div className="relative">
          <UserAvatar user={user} size={size} />
          {/* subtle “status” accent */}
          <span
            className="
              absolute -bottom-0.5 -right-0.5
              h-3 w-3 rounded-full
              bg-emerald-400/70
              ring-2 ring-black/50
              opacity-80
              group-hover:opacity-100
              transition-opacity
            "
            aria-hidden
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-semibold text-gray-100 tracking-tight truncate">
              Start a thread
            </p>

            {/* tiny divider chip */}
            <span
              className="
                h-1 w-1 rounded-full bg-white/30
                shrink-0
              "
              aria-hidden
            />
            <p className="text-xs text-gray-400 truncate">{activeTopicTag}</p>
          </div>

          <p className="text-[11px] text-gray-400/80 mt-1 truncate">
            Posting as{" "}
            <span className="text-gray-200/90 font-medium">{displayName}</span>
          </p>
        </div>

        <div
          className="
            relative h-10 w-10 shrink-0 rounded-2xl
            border border-cyan-400/30
            bg-cyan-400/10
            grid place-items-center
            text-cyan-400
            transition-all duration-200
            group-hover:bg-cyan-400/20
            group-hover:border-cyan-400/50
            overflow-hidden
          "
          aria-hidden
        >
          {/* subtle "futuristic" inner sheen */}
          <span
            className="
              absolute inset-0
              bg-gradient-to-br from-cyan-300/20 via-transparent to-transparent
              opacity-70
            "
            aria-hidden
          />
          <Plus className="relative h-4 w-4" />
        </div>
      </button>
    </div>
  );
}

/* ---------- helpers ---------- */

function getDisplayName(user) {
  if (!user) return "Guest";
  return user.username || user.handle || user.name || user.displayName || "User";
}

function UserAvatar({ user, size }) {
  const name = getDisplayName(user);
  const avatar = user?.avatarUrl || user?.profile_picture || user?.avatar || null;

  return avatar ? (
    <img
      src={avatar}
      alt={name}
      className="
        rounded-full object-cover object-center
        border border-white/10
        shadow-[0_10px_22px_rgba(0,0,0,0.35)]
      "
      style={{ width: size, height: size }}
      loading="lazy"
      draggable={false}
    />
  ) : (
    <div
      className="
        rounded-full border border-white/10
        bg-black/30
        grid place-items-center
        text-gray-200 font-semibold
        shadow-[0_10px_22px_rgba(0,0,0,0.35)]
      "
      style={{ width: size, height: size }}
      aria-label={name}
    >
      {name?.[0] || "U"}
    </div>
  );
}
